import React, { useCallback, useEffect, useRef, useState } from 'react'
import { WaveSurfer, WaveForm } from 'wavesurfer-react'

import { Avatar, Box, LinearProgress, makeStyles, Slider, withStyles } from '@material-ui/core'

import PlayArrowIcon from '@material-ui/icons/PlayArrow'
import PauseIcon from '@material-ui/icons/Pause'
import VolumeDownIcon from '@material-ui/icons/VolumeDown'
import VolumeUpIcon from '@material-ui/icons/VolumeUp'
import VolumeMuteIcon from '@material-ui/icons/VolumeMute'
import VolumeOffIcon from '@material-ui/icons/VolumeOff'

import { getTimeString } from 'modules'

const useStyles = makeStyles({
    playerContainer: {
        position: 'relative',
        width: '100%',
        height: '50px',
        backgroundColor: '#D53333',
        borderRadius: '4px',
        padding: '5px'
    },
    innerContainer: {
        display: 'flex',
        alignItems: 'center',
        height: '100%',
        width: '100%'
    },
    rightContainer: {
        position: 'relative',
        width: '100%'
    },
    waveContainer: {
        position: 'absolute',
        top: '-28px',
        left: '-10px',
        width: '100%',
        marginTop: '5px',
        height: '33px'
    },
    bottomLineContainer: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        position: 'absolute',
        top: '-3px',
        left: '-15px',
        width: '100%'
    },
    timeContainer: {
        fontWeight: 400,
        fontSize: '11px',
        color: '#FFFFFF',
        display: 'flex'
    },
    playStopButton: {
        backgroundColor: 'white',
        width: '40px',
        height: '40px',
        cursor: 'pointer',
        marginLeft: '5px',
        marginRight: '12px'
    },
    playStopIcon: {
        color: 'black'
    },
    icons: {
        width: '17px'
    }
})

const GuiddeSlider = withStyles({
    root: {
        color: '#ffffff'
    },
    thumb: {
        boxShadow: 'none',
        backgroundColor: 'transparent',
        '&:hover': {
            boxShadow: 'none  !important',
            backgroundColor: '#ffffff'
        },
        '&:focus': {
            boxShadow: 'none  !important'
        },
        '&:active': {
            boxShadow: 'none !important',
            backgroundColor: '#ffffff'
        },
        raili: {
            color: 'rgba(255, 255, 255, 0.5)',
            borderRadius: '2px'
        }
    }
})(Slider)

const SLIDER_WIDTH = 145

type waveProps = {
    audioFile: string
    onPlay?: () => void
}

export const AudioPlayer = ({ audioFile, onPlay }: waveProps) => {
    const classes = useStyles()

    const wavesurferRef = useRef<any | null>(null)

    const [volume, setVolume] = useState(0.5)
    const [currentTime, setCurrentTime] = useState(0)
    const [totalTime, setTotalTime] = useState(0)
    const [isPlaying, setIsPlaying] = useState(false)
    const [loaded, setLoaded] = useState(false)
    const [volumeIcon, setVolumeIcon] = useState(<VolumeDownIcon className={classes.icons} />)

    useEffect(() => {
        return () => wavesurferRef.current.destroy()
    }, [])

    useEffect(() => {
        if (!wavesurferRef.current) return
        // Load audio file
        wavesurferRef.current.load(audioFile)
    }, [audioFile])

    const handleWSMount = useCallback(
        (waveSurfer: any) => {
            wavesurferRef.current = waveSurfer

            // Wave is loaded
            wavesurferRef.current.on('ready', () => {
                setLoaded(true)
                setTotalTime(wavesurferRef.current.getDuration())
            })

            // Volume has changed
            // for volume 0 show VolumeOffIcon
            // for volume between 0 and 0.5 show  VolumeDownIcon
            // otherwise show VolumeUpIcon
            wavesurferRef.current.on('volume', (data: number) => {
                if (Number(data) === 0) {
                    setVolumeIcon(<VolumeOffIcon className={classes.icons} />)
                } else if (Number(data) > 0 && Number(data) <= 0.5) {
                    setVolumeIcon(<VolumeDownIcon className={classes.icons} />)
                } else {
                    setVolumeIcon(<VolumeUpIcon className={classes.icons} />)
                }
            })

            // Playing is done
            wavesurferRef.current.on('finish', () => {
                setIsPlaying(wavesurferRef.current.isPlaying())
            })

            //Volume button was clicked, toggle mute icon visibility
            wavesurferRef.current.on('mute', (data: boolean) => {
                data
                    ? setVolumeIcon(<VolumeOffIcon className={classes.icons} />)
                    : setVolumeIcon(<VolumeMuteIcon className={classes.icons} />)
            })

            // Update the running time and the total time
            wavesurferRef.current.on('audioprocess', () => {
                setCurrentTime(wavesurferRef.current.getCurrentTime())
            })
        },
        [classes.icons]
    )

    const containerRef = useRef<HTMLDivElement>(null)
    const [width, setWidth] = useState<string>('120px')

    useEffect(() => {
        // when the component gets mounted
        if (containerRef.current && containerRef.current.offsetWidth) {
            setWidth(`${containerRef?.current?.offsetWidth - SLIDER_WIDTH}px`)
        }
        // to handle page resize
        const getWidth = () => {
            if (containerRef.current && containerRef.current.offsetWidth) {
                setWidth(`${containerRef?.current?.offsetWidth - SLIDER_WIDTH}px`)
            }
        }
        window.addEventListener('resize', getWidth)
        // remove the event listener before the component gets unmounted
        return () => window.removeEventListener('resize', getWidth)
    }, [])

    const playPause = useCallback(() => {
        const isPlaying = wavesurferRef.current.isPlaying()

        wavesurferRef.current.playPause()
        setIsPlaying(!isPlaying)

        if (!isPlaying) onPlay?.()
    }, [onPlay])

    return (
        <Box id="player-container" className={classes.playerContainer} ref={containerRef}>
            <Box className={classes.innerContainer}>
                <Box>
                    <Avatar
                        onClick={playPause}
                        className={classes.playStopButton}
                        data-cy="editor-player-buttons"
                        data-intercom="editor-player-buttons"
                    >
                        {isPlaying ? (
                            <PauseIcon className={classes.playStopIcon} />
                        ) : (
                            <PlayArrowIcon className={classes.playStopIcon} />
                        )}
                    </Avatar>
                </Box>

                <Box className={classes.rightContainer}>
                    <Box className={classes.waveContainer}>
                        {!loaded && <LinearProgress style={{ top: '15px' }} />}
                        <WaveSurfer onMount={handleWSMount}>
                            <WaveForm
                                id="waveform"
                                // @ts-ignore
                                waveColor="#E58080"
                                progressColor="#FFFFFF"
                                cursorColor="#FFFFFF"
                                barWidth="2"
                                barRadius="3"
                                cursorWidth="1"
                                height="33"
                                barGap="1"
                                minPxPerSec={20}
                                fillParent
                                responsive
                                mediaControls
                                hideScrollbar
                            />
                        </WaveSurfer>
                    </Box>
                    <Box className={classes.bottomLineContainer}>
                        {/*volume icon*/}
                        <Box
                            style={{ color: 'white' }}
                            onClick={() => wavesurferRef.current.toggleMute()}
                        >
                            {volumeIcon}
                        </Box>

                        {/*volume slider*/}
                        <Box mt={0.5} pr={0.5} id="slider">
                            <GuiddeSlider
                                style={{ width: width }}
                                step={0.1}
                                min={0}
                                max={1}
                                value={volume}
                                onChange={(_e, newValue) => {
                                    setVolume(Number(newValue))
                                    wavesurferRef.current.setVolume(newValue)
                                }}
                            />
                        </Box>

                        {/*time*/}
                        <Box className={classes.timeContainer}>
                            <Box>{getTimeString(currentTime)}/</Box>
                            <Box>{getTimeString(totalTime)}</Box>
                        </Box>
                    </Box>
                </Box>
            </Box>
        </Box>
    )
}
